/**
 * About Us Page
 * Information about Mike Hanney and the TrainerDX project history
 */

import React from "react";
import {Card, CardBody, Row, Col} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import PageHeader from "../components/Common/PageHeader";
import cyclocrossRacing from "../assets/img/about-us/cyclocross-racing.jpg";
import zwiftHq from "../assets/img/about-us/zwift-hq.jpg";
import zwiftalizerV1 from "../assets/img/about-us/zwiftalizer-v1.jpg";

function AboutUs() {
  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <PageHeader
            title="About TrainerDX"
            subtitle="Meet the site owner and learn about the story behind TrainerDX"
            icon="fa fa-info-circle"
          />
          <Row>
            <Col md="12">
              <Card>
                <CardBody className="card-copy about-us">
                  <Row>
                    <Col xs="12" md={{size: 10, offset: 1}}>
                      <p>
                        <strong>
                          TrainerDX will shut down at midnight Pacific Time on November 30th, 2026. See the{" "}
                          <a href="/shutdown">shutdown notice</a> for details.
                        </strong>
                      </p>

                      <h2>Our Story</h2>
                      <p>
                        TrainerDX (formerly Zwiftalizer) has been helping Zwift users optimize their performance since
                        2016. What started as a personal project to solve my own technical issues has grown into a
                        community resource used by over 20,000 people across 50+ countries.
                      </p>

                      <h2>Why We Changed Our Name</h2>
                      <p>
                        In October 2025, we rebranded from Zwiftalizer to TrainerDX. This change was driven by a simple
                        principle: we want to respect Zwift's brand and intellectual property. While we've always been
                        an independent, community-driven tool, we recognize that using a name so similar to "Zwift"
                        could cause confusion or imply an official relationship that doesn't exist.
                      </p>
                      <p>
                        The new name, TrainerDX, better reflects what we do: we provide diagnostic analysis (DX) for
                        your indoor training setup. It was meant as a fresh start, staying true to our mission of
                        helping you get the most out of your virtual cycling experience.
                      </p>

                      <h2>About Mike Hanney</h2>
                      <div className="float-container float-right">
                        <img
                          src={zwiftHq}
                          alt="Mike Hanney at Zwift HQ in Long Beach, California"
                          className="magazine-img"
                        />
                        <p className="img-caption">
                          Visiting Zwift HQ in Long Beach, California - connecting with the community I serve
                        </p>
                      </div>

                      <p>
                        Hi, I'm Mike Hanney, the creator and maintainer of TrainerDX. I'm a Solutions Architect at a
                        Premier tier AWS Partner. I work with companies of all shapes and sizes desiging, building and
                        running cloud-based systems that scale.
                      </p>
                      <p>
                        But this project isn't about my professional credentials - it's about my passion for cycling and
                        solving real-world problems. I started TrainerDX (then Zwiftalizer) because I was frustrated
                        with technical issues in my own Zwift setup. I wanted to maximize performance while minimizing
                        cost and hassle-factor, and figured others had the same goals.
                      </p>

                      <h2>How It Started</h2>
                      <p>
                        The first version of Zwiftalizer was incredibly basic - just a simple log parser that showed
                        basic metrics like FPS and ANT+ signals. I built it for myself and shared it with the friends in
                        the Zwift community. Word spread, and before I knew it, people I'd never met were using it to
                        troubleshoot their own issues.
                      </p>

                      <img
                        src={zwiftalizerV1}
                        alt="First version of Zwiftalizer from 2016"
                        className="magazine-img screenshot"
                      />
                      <p className="img-caption">
                        The original Zwiftalizer interface from 2016 - simple, functional, and focused on the essentials
                      </p>

                      <p>
                        Over the years, the tool evolved from a basic log parser into a comprehensive diagnostic
                        platform. I added benchmark comparisons to show how your hardware stacks up, network analysis to
                        troubleshoot connectivity issues, search capabilities to find specific hardware configs, and
                        most recently, AI-powered "Advanced Analysis" that correlates events across your logs to
                        pinpoint root causes and suggest fixes. Every feature was built to solve real problems users
                        reported. This has always been a community-driven project.
                      </p>

                      <img
                        src="/img/screenshots/02.webp"
                        alt="Modern TrainerDX interface with Advanced Analysis"
                        className="magazine-img screenshot"
                      />
                      <p className="img-caption">
                        The evolved interface - comprehensive data visualization with AI-powered Advanced Analysis
                      </p>

                      <h2>Why We Went Freemium</h2>
                      <p>
                        For the first several years, TrainerDX was completely free. I covered all the development and
                        hosting costs myself because I wanted the tool to be accessible to everyone. But as the user
                        base grew and features became more sophisticated (especially the advanced analysis), the
                        infrastructure costs became unsustainable.
                      </p>
                      <p>
                        Rather than put the entire service behind a paywall or show ads, I chose a freemium model:
                        everyone gets 5 free advanced analyses per month, and power users who need more can purchase
                        additional tokens. This way, casual users can still benefit from the service without paying
                        anything, while those who get serious value from the tool can help support its continued
                        development.
                      </p>
                      <p>
                        Until the site closes, the benchmark database, search tool, ping tool and help files remain
                        available to everyone at no cost.
                      </p>
                      <p>
                        As part of winding the site down, new registrations and token purchases are no longer available.
                      </p>

                      <h2>About Torque Advisory LLC</h2>
                      <div className="float-container float-right">
                        <img
                          src={cyclocrossRacing}
                          alt="Mike racing cyclocross at Woodland Park, Seattle"
                          className="magazine-img"
                        />
                        <p className="img-caption">
                          Racing cyclocross at Woodland Park, Seattle - where my passion for cycling meets competition
                        </p>
                      </div>
                      <p>
                        You might notice that TrainerDX is operated by Torque Advisory LLC. This is simply the formal
                        business structure I use to manage this project. Operating under an LLC provides basic liability
                        protection and makes it easier to handle business aspects like payment processing and taxes.
                      </p>
                      <p>
                        Don't let the corporate name fool you - this is still a one-person operation. I handle
                        everything from feature development to cloud operations to customer support. Torque Advisory LLC
                        is simply the legal structure that protects my personal assets if someone sues over a payment
                        dispute, data handling claim, or any other business issue. It's standard practice for anyone
                        running a service that processes payments and handles user data.
                      </p>

                      <h2>Our Community</h2>
                      <p>
                        TrainerDX exists because of its community. Over the years, users have suggested features,
                        reported bugs, shared their success stories, and helped other users troubleshoot issues. Some of
                        the best features in TrainerDX came directly from user suggestions.
                      </p>
                      <p>
                        Whether you're a new Zwift user trying to figure out why your frame rate is low, or a long-time
                        indoor cyclist optimizing your setup for the best possible experience, TrainerDX has been here
                        to help.
                      </p>

                      <h2>The End of the Road</h2>
                      <p>
                        After ten years, I've decided to retire TrainerDX. Hardware has improved and Bluetooth has made
                        device connectivity far more reliable, so the problems TrainerDX was built to diagnose have
                        become much less common. I also no longer have the time to maintain the site. You can read more
                        in the <a href="/shutdown">shutdown notice</a>, including what happens to your data.
                      </p>
                      <p>
                        TrainerDX was never owned by a large company. It never tried to sell you hardware or push you
                        toward specific products, and I never sold your data. It was just a tool built by a cyclist, for
                        cyclists.
                      </p>

                      <h2>Get In Touch</h2>
                      <p>
                        I love hearing from users! If you have questions, suggestions, or just want to share your
                        TrainerDX success story, you can reach me through the <a href="/contact">contact form</a>. I
                        read every message, though it sometimes takes me a few days to respond during busy periods.
                      </p>

                      <hr className="my-4" />

                      <p className="text-center text-muted">
                        <em>Thank you for being part of the TrainerDX community!</em>
                      </p>
                      <p className="text-center text-muted">
                        <strong>- Mike Hanney</strong>
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default AboutUs;
